import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc,
  setDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// In-memory cache to reduce Firebase requests
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

// Cache helper functions
const getCacheKey = (fn, userId, ...args) => `${fn}_${userId}_${args.join('_')}`;

const getFromCache = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Save a completed Pomodoro session
export const savePomodoroSession = async (userId, sessionData) => {
  try {
    // Fixed: Use 3-segment path (ODD) for collection - users/{userId}/pomodoroSessions
    const sessionsRef = collection(db, 'users', userId, 'pomodoroSessions');
    const sessionDoc = await addDoc(sessionsRef, {
      start: sessionData.start,
      end: serverTimestamp(),
      duration: sessionData.duration,
      type: sessionData.type || 'work', // 'work', 'shortBreak', 'longBreak'
      createdAt: serverTimestamp()
    });
    
    // Clear related caches
    const cacheKeys = Array.from(cache.keys()).filter(key => 
      key.includes(`getTodayPomodoroSessions_${userId}`) || 
      key.includes(`getWeeklyPomodoroSessions_${userId}`)
    );
    cacheKeys.forEach(key => cache.delete(key));
    
    console.log('Pomodoro session saved:', sessionDoc.id);
    return sessionDoc.id;
  } catch (error) {
    console.error('Error saving pomodoro session:', error);
    throw error;
  }
};

// Get today's Pomodoro sessions with caching
export const getTodayPomodoroSessions = async (userId) => {
  if (!userId) {
    console.warn('No userId provided to getTodayPomodoroSessions');
    return [];
  }

  const cacheKey = getCacheKey('getTodayPomodoroSessions', userId);
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('📋 Using cached today sessions');
    return cached;
  }

  try {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    
    // Fixed: Use 3-segment path (ODD) for collection
    const sessionsRef = collection(db, 'users', userId, 'pomodoroSessions');
    const q = query(
      sessionsRef,
      where('createdAt', '>=', startOfToday),
      where('createdAt', '<', endOfToday),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const sessions = [];
    querySnapshot.forEach((doc) => {
      sessions.push({ id: doc.id, ...doc.data() });
    });
    
    setCache(cacheKey, sessions);
    console.log(`✅ Loaded ${sessions.length} today sessions from Firebase`);
    return sessions;
  } catch (error) {
    console.error('Error fetching today\'s pomodoro sessions:', error);
    // Return empty array instead of throwing to prevent cascading failures
    return [];
  }
};

// Get weekly Pomodoro sessions for analytics with caching
export const getWeeklyPomodoroSessions = async (userId) => {
  if (!userId) {
    console.warn('No userId provided to getWeeklyPomodoroSessions');
    return { sessions: [], dailyStats: {} };
  }

  const cacheKey = getCacheKey('getWeeklyPomodoroSessions', userId);
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('📊 Using cached weekly sessions');
    return cached;
  }

  try {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // Fixed: Use 3-segment path (ODD) for collection
    const sessionsRef = collection(db, 'users', userId, 'pomodoroSessions');
    const q = query(
      sessionsRef,
      where('createdAt', '>=', weekAgo),
      where('createdAt', '<=', today),
      orderBy('createdAt', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const sessions = [];
    querySnapshot.forEach((doc) => {
      sessions.push({ id: doc.id, ...doc.data() });
    });
    
    // Group sessions by day for chart data
    const dailyStats = {};
    sessions.forEach(session => {
      const date = session.createdAt?.toDate?.()?.toDateString?.() || new Date().toDateString();
      if (!dailyStats[date]) {
        dailyStats[date] = 0;
      }
      dailyStats[date]++;
    });
    
    const result = { sessions, dailyStats };
    setCache(cacheKey, result);
    console.log(`✅ Loaded ${sessions.length} weekly sessions from Firebase`);
    return result;
  } catch (error) {
    console.error('Error fetching weekly pomodoro sessions:', error);
    // Return empty data instead of throwing to prevent cascading failures
    return { sessions: [], dailyStats: {} };
  }
};

// Save Pomodoro settings with caching
export const savePomodoroSettings = async (userId, settings) => {
  if (!userId) {
    console.warn('No userId provided to savePomodoroSettings');
    return;
  }

  try {
    // Fixed: Use 4-segment path (EVEN) for document - users/{userId}/app/pomodoroSettings
    const settingsRef = doc(db, 'users', userId, 'app', 'pomodoroSettings');
    
    // Default time objects
    const defaultWorkDuration = { hours: 0, minutes: 25, seconds: 0 };
    const defaultShortBreakDuration = { hours: 0, minutes: 5, seconds: 0 };
    const defaultLongBreakDuration = { hours: 0, minutes: 15, seconds: 0 };
    
    const settingsData = {
      workDuration: settings.workDuration || defaultWorkDuration,
      shortBreakDuration: settings.shortBreakDuration || defaultShortBreakDuration,
      longBreakDuration: settings.longBreakDuration || defaultLongBreakDuration,
      longBreakInterval: settings.longBreakInterval || 4,
      notificationsEnabled: settings.notificationsEnabled !== undefined ? settings.notificationsEnabled : false,
      soundEnabled: settings.soundEnabled !== undefined ? settings.soundEnabled : false,
      autoStartBreaks: settings.autoStartBreaks !== undefined ? settings.autoStartBreaks : true,
      autoStartSessions: settings.autoStartSessions !== undefined ? settings.autoStartSessions : false,
      currentStreak: settings.currentStreak !== undefined ? settings.currentStreak : 0,
      todaySessionCount: settings.todaySessionCount !== undefined ? settings.todaySessionCount : 0,
      updatedAt: serverTimestamp()
    };
    
    await setDoc(settingsRef, settingsData);
    
    // Clear settings cache
    const cacheKeys = Array.from(cache.keys()).filter(key => 
      key.includes(`getPomodoroSettings_${userId}`) || 
      key.includes(`getPomodoroSettingsSummary_${userId}`)
    );
    cacheKeys.forEach(key => cache.delete(key));
    
    console.log('Pomodoro settings saved successfully');
  } catch (error) {
    console.error('Error saving pomodoro settings:', error);
    throw error;
  }
};

// Get Pomodoro settings with caching
export const getPomodoroSettings = async (userId) => {
  if (!userId) {
    console.warn('No userId provided to getPomodoroSettings');
    return getDefaultSettings();
  }

  const cacheKey = getCacheKey('getPomodoroSettings', userId);
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log('⚙️ Using cached settings');
    return cached;
  }

  try {
    // Fixed: Use 4-segment path (EVEN) for document
    const settingsRef = doc(db, 'users', userId, 'app', 'pomodoroSettings');
    const settingsDoc = await getDoc(settingsRef);
    
    let result;
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      
      // Handle backward compatibility - convert old minute format to new time object format
      const convertToTimeObject = (value, defaultMinutes) => {
        if (typeof value === 'object' && value.hours !== undefined) {
          return value; // Already in new format
        }
        return { hours: 0, minutes: value || defaultMinutes, seconds: 0 }; // Convert from old format
      };
      
      result = {
        workDuration: convertToTimeObject(data.workDuration, 25),
        shortBreakDuration: convertToTimeObject(data.shortBreakDuration, 5),
        longBreakDuration: convertToTimeObject(data.longBreakDuration, 15),
        longBreakInterval: data.longBreakInterval || 4,
        notificationsEnabled: data.notificationsEnabled !== undefined ? data.notificationsEnabled : false,
        soundEnabled: data.soundEnabled !== undefined ? data.soundEnabled : false,
        autoStartBreaks: data.autoStartBreaks !== undefined ? data.autoStartBreaks : true,
        autoStartSessions: data.autoStartSessions !== undefined ? data.autoStartSessions : false,
        currentStreak: data.currentStreak !== undefined ? data.currentStreak : 0,
        todaySessionCount: data.todaySessionCount !== undefined ? data.todaySessionCount : 0
      };
    } else {
      // Return default settings in new format
      result = getDefaultSettings();
    }
    
    setCache(cacheKey, result);
    console.log('✅ Loaded settings from Firebase');
    return result;
  } catch (error) {
    console.error('Error fetching pomodoro settings:', error);
    // Return defaults instead of throwing to prevent cascading failures
    return getDefaultSettings();
  }
};

// Default settings helper
const getDefaultSettings = () => ({
  workDuration: { hours: 0, minutes: 25, seconds: 0 },
  shortBreakDuration: { hours: 0, minutes: 5, seconds: 0 },
  longBreakDuration: { hours: 0, minutes: 15, seconds: 0 },
  longBreakInterval: 4,
  notificationsEnabled: false,
  soundEnabled: false,
  autoStartBreaks: true,
  autoStartSessions: false,
  currentStreak: 0,
  todaySessionCount: 0
});

// Reset Pomodoro settings to defaults
export const resetPomodoroSettings = async (userId) => {
  if (!userId) {
    console.warn('No userId provided to resetPomodoroSettings');
    return getDefaultSettings();
  }

  try {
    // Fixed: Use 4-segment path (EVEN) for document
    const settingsRef = doc(db, 'users', userId, 'app', 'pomodoroSettings');
    
    const defaultSettings = {
      ...getDefaultSettings(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(settingsRef, defaultSettings);
    
    // Clear settings cache
    const cacheKeys = Array.from(cache.keys()).filter(key => 
      key.includes(`getPomodoroSettings_${userId}`) || 
      key.includes(`getPomodoroSettingsSummary_${userId}`)
    );
    cacheKeys.forEach(key => cache.delete(key));
    
    console.log('Pomodoro settings reset to defaults');
    return defaultSettings;
  } catch (error) {
    console.error('Error resetting pomodoro settings:', error);
    throw error;
  }
};

// Get settings summary for user with caching
export const getPomodoroSettingsSummary = async (userId) => {
  if (!userId) {
    return null;
  }

  const cacheKey = getCacheKey('getPomodoroSettingsSummary', userId);
  const cached = getFromCache(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const settings = await getPomodoroSettings(userId);
    
    const timeToString = (timeObj) => {
      const parts = [];
      if (timeObj.hours > 0) parts.push(`${timeObj.hours}h`);
      if (timeObj.minutes > 0) parts.push(`${timeObj.minutes}m`);
      if (timeObj.seconds > 0) parts.push(`${timeObj.seconds}s`);
      return parts.join(' ') || '0s';
    };
    
    const result = {
      workDuration: timeToString(settings.workDuration),
      shortBreakDuration: timeToString(settings.shortBreakDuration),
      longBreakDuration: timeToString(settings.longBreakDuration),
      longBreakInterval: settings.longBreakInterval,
      notificationsEnabled: settings.notificationsEnabled,
      soundEnabled: settings.soundEnabled,
      autoStartBreaks: settings.autoStartBreaks,
      autoStartSessions: settings.autoStartSessions,
      currentStreak: settings.currentStreak,
      todaySessionCount: settings.todaySessionCount
    };
    
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Error getting pomodoro settings summary:', error);
    return null;
  }
};

// Clear all caches (useful for debugging)
export const clearPomodoroCache = () => {
  cache.clear();
  console.log('🧹 Pomodoro cache cleared');
}; 